import { useEffect, useState } from 'react';
import { Dialog, Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ReplayIcon from '@mui/icons-material/Replay';
import { NftsWithUserOwnedType } from '../../../../types/types';
import './CardInspectModal.css';

// Card art lives in the public assets bucket (same source the arena uses via a
// wsrv proxy for its WebGL textures — a plain <img> needs no CORS so we hit it
// directly). THE CDN FIRST (owner 2026-10-29): the Digital Collectibles CloudFront,
// whose origin is the bucket's digital-collectibles/ folder; REACT_APP_NFT_CLOUDFRONT
// overrides it. The bucket's own URL is the FALLBACK an <img> swaps to once if the
// CDN fails (cardArtFallback / onCardArtError) — never the first request.
export const CARD_ART_BASE =
  process.env.REACT_APP_NFT_CLOUDFRONT || 'https://d29jxjsfxcaexh.cloudfront.net';
export const CARD_ART_FALLBACK_BASE =
  'https://incrypt-public-assets-global.s3.ap-southeast-2.amazonaws.com/digital-collectibles';
/* Swap a failed card <img> to the bucket copy, once. True when it did (the caller waits for
   that load); false when it already had (the caller may hide the image). */
export const cardArtFallback = (img: HTMLImageElement): boolean => {
  if (img.dataset.artFallback || !img.src.startsWith(CARD_ART_BASE)) return false;
  img.dataset.artFallback = '1';
  img.src = CARD_ART_FALLBACK_BASE + img.src.slice(CARD_ART_BASE.length);
  return true;
};
export const onCardArtError = (e: React.SyntheticEvent<HTMLImageElement>): void => {
  cardArtFallback(e.currentTarget);
};

/* "naval ravikant" → "Naval Ravikant": card names are stored lowercase and shown with
   each word capitalised (owner, 2026-10-22 — tried capitals, chose this). The ONE rule for
   every place a card is named: the collection, the marketplace, the public catalogue, the
   deck builder, the pack reveal (its own copy, packRevealEngine.cardTitle — the arena
   bundle cannot import React code) and the share page. Display only: never feed it to an
   art URL, a search or a comparison — those use the stored name. */
export const cardTitle = (s: string) => String(s || '').replace(/(^|[\s(\-\/.])([a-z])/g, (m, p, c) => p + c.toUpperCase());

export const cardArtUrl = (nft: {
  type: string;
  name: string;
  rarity: string;
}): string => `${CARD_ART_BASE}/${nft.type}/${nft.name}/${nft.rarity}.png`;

const RARITY_HEX: Record<string, string> = {
  common: '#8b919c',
  rare: '#5b9bd5',
  epic: '#a06fff',
  legendary: '#ffcf40',
  relic: '#38e8ff'
};
const rarityHex = (r?: string): string =>
  RARITY_HEX[String(r || '').toLowerCase()] || '#8b919c';

type CardInspectModalProps = {
  card: NftsWithUserOwnedType | null;
  onClose: () => void;
  onBuy: (card: NftsWithUserOwnedType) => void;
};

export const CardInspectModal = ({
  card,
  onClose,
  onBuy
}: CardInspectModalProps) => {
  const [spinning, setSpinning] = useState(false);

  // Kick off a spin whenever a new card is opened.
  useEffect(() => {
    if (card) setSpinning(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card?.nft?.id]);

  const nft = card?.nft;
  const isUnit =
    !!nft && (nft.type === 'character' || nft.type === 'influencer');
  const accent = rarityHex(nft?.rarity);

  // scroll="body": the paper must not clip (the 3D card floats past it — see
  // .cim-paper), so on a short phone a dialog taller than the screen spilled
  // past the bottom with no way to reach its buttons. Now the whole dialog
  // scrolls instead.
  return (
    <Dialog
      open={Boolean(card)}
      onClose={onClose}
      fullWidth
      scroll="body"
      PaperProps={{ className: 'cim-paper' }}
    >
      {nft && card && (
        <Box className="cim">
          <IconButton
            className="cim-close"
            onClick={onClose}
            aria-label="Close"
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          <div className="cim-stage">
            <div
              className={`cim-card${spinning ? ' spinning' : ''}`}
              onClick={() => setSpinning(true)}
              onAnimationEnd={() => setSpinning(false)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setSpinning(true);
                }
              }}
              title="Spin"
            >
              <div
                className="cim-face cim-front"
                style={{
                  borderColor: accent,
                  boxShadow: `0 0 34px -10px ${accent}`
                }}
              >
                <img src={cardArtUrl(nft)} alt={nft.name} onError={onCardArtError} />
              </div>
              <div className="cim-face cim-back" />
            </div>
          </div>

          <div className="cim-info">
            <div className="cim-name">{cardTitle(nft.name)}</div>

            <div className="cim-chips">
              <span
                className="cim-chip"
                style={{ color: accent, borderColor: `${accent}66` }}
              >
                {nft.rarity}
              </span>
              {nft.faction && <span className="cim-chip">{nft.faction}</span>}
              <span className="cim-chip">{nft.type}</span>
            </div>

            <div className="cim-stats">
              {isUnit ? (
                <>
                  <div className="cim-stat">
                    <div className="v" style={{ color: '#ffcf40' }}>
                      {nft.attack}
                    </div>
                    <div className="k">Attack</div>
                  </div>
                  <div className="cim-stat">
                    <div className="v" style={{ color: '#ff5d5d' }}>
                      {nft.defence}
                    </div>
                    <div className="k">Defence</div>
                  </div>
                </>
              ) : (
                <div className="cim-stat cim-stat-wide">
                  <div className="v" style={{ color: '#42e6a4' }}>
                    +{nft.boost}
                  </div>
                  <div className="k">Boost</div>
                </div>
              )}
            </div>

            <div
              className="cim-owned"
              style={{ color: card.owned > 0 ? '#42e6a4' : '#5c6b78' }}
            >
              {card.owned > 0
                ? `You own ×${card.owned}`
                : 'Not in your collection yet'}
            </div>

            <div className="cim-actions">
              <button
                type="button"
                className="cim-btn cim-back-btn"
                onClick={onClose}
              >
                <ArrowBackIosNewIcon sx={{ fontSize: 13 }} /> Back
              </button>
              <button
                type="button"
                className="cim-btn cim-spin-btn"
                onClick={() => setSpinning(true)}
              >
                <ReplayIcon sx={{ fontSize: 16 }} /> Spin
              </button>
              <button
                type="button"
                className="cim-btn cim-buy-btn"
                onClick={() => onBuy(card)}
              >
                Buy on Marketplace
              </button>
            </div>
          </div>
        </Box>
      )}
    </Dialog>
  );
};
